import {useEffect, useRef} from 'react';
import {Link} from 'react-router-dom';
import {Splide} from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import 'viewerjs/dist/viewer.min.css';
import Viewer from 'viewerjs';
import Layout from '../components/home/Layout';
import StoreButtons from '../components/home/StoreButtons';
import {apps} from '../data/apps';
import {appPages} from '../data/appPages';
import {renderSlides} from '../utils/slides';
import {useLang} from '../hooks/useLang';
import {appJsonLd} from '../services/structuredData';
import {ogLanguage} from '../services/language';

const PHONE_SLIDER = {
  type: 'loop',
  pagination: false,
  arrows: true,
  perPage: 4,
  perMove: 1,
  autoplay: true,
  interval: 3000,
  gap: 16,
  breakpoints: {
    767: {
      perPage: 2,
      arrows: false,
    },
  },
};

// Landscape screenshots (the web version): two per view, one on mobile
const WIDE_SLIDER = {
  ...PHONE_SLIDER,
  perPage: 2,
  breakpoints: {
    767: {
      perPage: 1,
      arrows: false,
    },
  },
};

const VIEWER_OPTIONS = {
  movable: false,
  rotatable: false,
  tooltip: false,
  toolbar: false,
  navbar: false,
  title: false,
  zoomOnTouch: false,
  zoomOnWheel: false,
  toggleOnDblclick: false,
};

// Screenshot carousel; tap on a screenshot opens it full screen
const Gallery = ({screens, label}) => {
  const ref = useRef(null);

  useEffect(() => {
    const list = ref.current && ref.current.querySelector('.splide__list');
    if (!list) return undefined;
    const viewer = new Viewer(list, VIEWER_OPTIONS);
    return () => viewer.destroy();
  }, []);

  return (
    <div className={`app-landing__gallery ${screens.wide ? 'app-landing__gallery--wide' : ''}`} ref={ref}>
      <Splide options={screens.wide ? WIDE_SLIDER : PHONE_SLIDER} aria-label={label}>
        {renderSlides(screens.count, screens.folder)}
      </Splide>
    </div>
  );
};

const Features = ({items}) => (
  <ul className="features">
    {items.map((feature) => (
      <li className="features__item" key={feature}>
        <span className="features__dot" aria-hidden="true"/>
        {feature}
      </li>
    ))}
  </ul>
);

// New-style page of an app: header, icon and stats, store buttons,
// the screenshot carousel and a short list of features
const AppPage = ({id, section}) => {
  const {lang, t} = useLang();
  const app = apps.find((item) => item.id === id);
  const page = appPages[id];
  const name = t(`apps.${id}.name`);
  const text = (key, options) => t(`appPages.${id}.${key}`, options);
  // The Physics policy exists in Russian and English
  const privacy = typeof page.privacy === 'string' ? page.privacy : page.privacy[lang] || page.privacy.en;

  // /pt-web opens this page at the web version block
  useEffect(() => {
    const target = section && document.getElementById(section);
    if (target) target.scrollIntoView();
  }, [section]);

  return (
    <Layout
      lang={lang}
      seo={{
        // /pt-web shows the same page, so it points search engines at /pt-mobile
        path: app.page,
        title: `${text('title')} | chernykh.tech`,
        description: text('description'),
        image: `${app.ogImage}-${ogLanguage(lang)}.png`,
        imageAlt: name,
        jsonLd: appJsonLd(lang, t, app),
      }}
    >
      <div className={`app-landing app-landing--${id}`}>
        <section className="app-landing__intro">
          <div className="app-landing__head">
            <img
              className="app-landing__icon"
              src={app.icon.src2x}
              width="96"
              height="96"
              alt={name}
            />
            <div>
              <h1 className="app-landing__name">{name}</h1>
              <div className="app-landing__stats">
                <span className="app-landing__stat app-landing__stat--downloads" title={t(`apps.${id}.downloads`)}>
                  {app.downloadsCount}
                </span>
                <span
                  className="app-landing__stat app-landing__stat--rating"
                  title={`${t('common.rating')} ${app.rating}`}
                >
                  {app.ratingsCount}
                </span>
              </div>
            </div>
          </div>
          <StoreButtons appStore={app.appStore} googlePlay={app.googlePlay}/>
          <p className="app-landing__lead">{text('lead')}</p>
        </section>

        <Gallery screens={page.screens} label={name}/>

        <section className="app-landing__features">
          <h2 className="app-landing__title">{text('featuresTitle')}</h2>
          <Features items={text('features', {returnObjects: true})}/>
        </section>

        {(page.sections || []).map((block) => {
          const blockText = (key, options) => text(`sections.${block.id}.${key}`, options);
          return (
            <section className="app-landing__section" id={block.id} key={block.id}>
              <h2 className="app-landing__section-title">{blockText('title')}</h2>
              {block.link ? (
                <a className="app-landing__stat app-landing__stat--link" href={block.link} target="_blank" rel="noreferrer">
                  {block.link.replace(/^https?:\/\/|\/$/g, '')}
                </a>
              ) : null}
              <p className="app-landing__lead">{blockText('lead')}</p>
              <Gallery screens={block.screens} label={blockText('title')}/>
              <Features items={blockText('features', {returnObjects: true})}/>
            </section>
          );
        })}

        <nav className="app-landing__links">
          {page.faq ? <Link to={page.faq} onClick={() => window.scrollTo(0, 0)}>{text('faq')}</Link> : null}
          <Link to={privacy} onClick={() => window.scrollTo(0, 0)}>{text('privacy')}</Link>
        </nav>
      </div>
    </Layout>
  );
};

export default AppPage;
